import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react'
import { makeStyles } from '@material-ui/core'
import Typography from '@material-ui/core/Typography'
import PlayArrowIcon from '@material-ui/icons/PlayArrow'
import PauseIcon from '@material-ui/icons/Pause'
import useAudioPlayer from './hooks/useAudioPlayer'
import { evaluateInteractive } from './hooks/useLessonScoring'
import SceneBackground from './SceneBackground'
import { CapeeshColors } from '../../../../assets/ColorPalette'
import renderFormattedText from './utils/renderFormattedText'

const InteractiveElementType = {
    MultiChoice: 1,
    ListenButton: 6,
    ListenButtonWithSlider: 8,
}

const AudioPlayType = {
    DontPlay: 0,
    PlayBefore: 1,
    PlayAfter: 2,
}

const useStyles = makeStyles((theme) => ({
    container: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        width: '100%',
        height: '100%',
        gap: 16,
        padding: '12px 20px 20px',
        overflow: 'auto',
        '@media (min-width: 600px) and (orientation: landscape)': {
            gap: 10,
            padding: '8px 16px 16px',
        },
        '@media (max-width: 599px)': {
            padding: '4px 10px 12px',
            gap: 10,
        },
    },
    sceneContainer: {
        width: '100%',
        maxWidth: 600,
        flexShrink: 0,
        minHeight: 100,
        borderRadius: 16,
        '@media (min-width: 600px) and (orientation: landscape)': {
            maxWidth: 'min(600px, 45vh)',
        },
        '@media (max-width: 599px)': {
            borderRadius: 10,
        },
    },
    headerSection: {
        width: '100%',
        maxWidth: 480,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: 8,
        flexShrink: 0,
    },
    titleText: {
        fontFamily: 'Rubik',
        fontSize: 20,
        fontWeight: 600,
        color: '#FFFFFF',
        textAlign: 'center',
        '@media (min-width: 600px) and (orientation: landscape)': {
            fontSize: 17,
        },
    },
    progressText: {
        fontFamily: 'Rubik',
        fontSize: 13,
        fontWeight: 400,
        color: 'rgba(255, 255, 255, 0.5)',
    },
    questionCard: {
        width: '100%',
        maxWidth: 480,
        backgroundColor: 'rgba(255, 255, 255, 0.08)',
        borderRadius: 20,
        padding: '24px 20px',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: 16,
        flexShrink: 0,
        '@media (min-width: 600px) and (orientation: landscape)': {
            padding: '16px 16px',
            gap: 10,
        },
        '@media (max-width: 599px)': {
            maxWidth: '100%',
            padding: '14px 14px',
            borderRadius: 14,
            gap: 10,
        },
    },
    questionImage: {
        maxWidth: '100%',
        maxHeight: 200,
        borderRadius: 12,
        objectFit: 'contain',
        '@media (min-width: 600px) and (orientation: landscape)': {
            maxHeight: '20vh',
        },
    },
    questionText: {
        fontFamily: 'Rubik',
        fontSize: 18,
        fontWeight: 500,
        color: '#FFFFFF',
        textAlign: 'center',
        '@media (min-width: 600px) and (orientation: landscape)': {
            fontSize: 15,
        },
    },
    listenRow: {
        display: 'flex',
        alignItems: 'center',
        gap: 14,
        width: '100%',
        maxWidth: 480,
        padding: '16px 20px',
        backgroundColor: 'rgba(255, 255, 255, 0.1)',
        borderRadius: 14,
        backdropFilter: 'blur(8px)',
        flexShrink: 0,
        '@media (max-width: 599px)': {
            padding: '14px 16px',
            gap: 10,
        },
    },
    listenButton: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        width: 44,
        height: 44,
        borderRadius: '50%',
        border: 'none',
        backgroundColor: CapeeshColors.BlueBright,
        cursor: 'pointer',
        flexShrink: 0,
    },
    listenIcon: {
        fontSize: 22,
        color: '#FFFFFF',
    },
    sliderContainer: {
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
        gap: 4,
    },
    sliderTrack: {
        width: '100%',
        height: 4,
        borderRadius: 2,
        backgroundColor: 'rgba(255, 255, 255, 0.2)',
        overflow: 'hidden',
    },
    sliderFill: {
        height: '100%',
        backgroundColor: CapeeshColors.BlueBright,
        transition: 'width 100ms linear',
    },
    timeLabel: {
        fontFamily: 'Rubik',
        fontSize: 11,
        fontWeight: 400,
        color: 'rgba(255, 255, 255, 0.5)',
    },
    optionsContainer: {
        display: 'flex',
        flexDirection: 'column',
        gap: 10,
        width: '100%',
        maxWidth: 480,
        flexShrink: 0,
        '@media (min-width: 600px) and (orientation: landscape)': {
            gap: 8,
        },
        '@media (max-width: 599px)': {
            maxWidth: '100%',
            gap: 8,
        },
    },
    optionButton: {
        width: '100%',
        padding: '14px 18px',
        borderRadius: 14,
        border: '2px solid rgba(255, 255, 255, 0.3)',
        backgroundColor: 'rgba(255, 255, 255, 0.08)',
        color: '#FFFFFF',
        fontFamily: 'Rubik',
        fontSize: 15,
        fontWeight: 500,
        textAlign: 'left',
        cursor: 'pointer',
        transition: 'all 200ms ease',
        '&:hover': {
            backgroundColor: 'rgba(255, 255, 255, 0.16)',
            borderColor: 'rgba(255, 255, 255, 0.5)',
        },
        '@media (min-width: 600px) and (orientation: landscape)': {
            fontSize: 14,
            padding: '11px 14px',
        },
        '@media (max-width: 599px)': {
            padding: '11px 14px',
            fontSize: 14,
            borderRadius: 12,
        },
    },
    optionCorrect: {
        borderColor: CapeeshColors.GreenBright,
        backgroundColor: 'rgba(128, 211, 135, 0.25)',
        '&:hover': {
            backgroundColor: 'rgba(128, 211, 135, 0.25)',
        },
    },
    optionIncorrect: {
        borderColor: CapeeshColors.PinkBright,
        backgroundColor: 'rgba(255, 102, 110, 0.25)',
        '&:hover': {
            backgroundColor: 'rgba(255, 102, 110, 0.25)',
        },
    },
    optionDimmed: {
        opacity: 0.4,
        cursor: 'default',
        '&:hover': {
            backgroundColor: 'rgba(255, 255, 255, 0.08)',
            borderColor: 'rgba(255, 255, 255, 0.3)',
        },
    },
    optionExamSelected: {
        borderColor: 'rgba(255, 255, 255, 0.45)',
        backgroundColor: 'rgba(255, 255, 255, 0.15)',
        '&:hover': {
            backgroundColor: 'rgba(255, 255, 255, 0.15)',
        },
    },
    feedbackText: {
        fontFamily: 'Rubik',
        fontSize: 14,
        fontWeight: 500,
        textAlign: 'center',
        '@media (min-width: 600px) and (orientation: landscape)': {
            fontSize: 12,
        },
    },
    feedbackCorrect: {
        color: CapeeshColors.GreenBright,
    },
    feedbackIncorrect: {
        color: CapeeshColors.PinkBright,
    },
    continueButton: {
        padding: '14px 48px',
        borderRadius: 28,
        border: 'none',
        backgroundColor: '#FFFFFF',
        color: '#1A1A2E',
        fontFamily: 'Rubik',
        fontSize: 16,
        fontWeight: 600,
        cursor: 'pointer',
        boxShadow: '0px 4px 16px rgba(0, 0, 0, 0.15)',
        marginTop: 16,
        transition: 'transform 150ms ease',
        '&:hover': {
            transform: 'translateY(-1px)',
        },
    },
}))

function formatTime(seconds) {
    if (!isFinite(seconds) || isNaN(seconds)) return '0:00'
    const m = Math.floor(seconds / 60)
    const s = Math.floor(seconds % 60)
    return `${m}:${s.toString().padStart(2, '0')}`
}

/**
 * MultiChoiceNode — dedicated component for standalone multi-choice questions.
 * Type 15 (MultiChoice): iterates through questions from testOptions.
 * Type 16 (ListenMultiChoice): additionally shows a listen-with-slider audio player.
 *
 * Matches Unity ScenarioMultiChoiceNodeController behavior:
 *  - Wrong answers don't advance; the selected wrong alternative is disabled and the user retries.
 *  - Correct answer (or any answer in exam mode) advances to the next question.
 *  - All attempts are tracked for scoring.
 *  - PlayAfter audio is played on correct answers when configured.
 */
const MultiChoiceNode = ({ node, previousNode, imageMap, audioMap, onNodeComplete, isExamMode, isNativeSpeaker = false, l2LanguageId }) => {
    const classes = useStyles()
    const audioPlayer = useAudioPlayer()
    const playAfterAudioPlayer = useAudioPlayer()
    const questionStartTime = useRef(Date.now())

    const animationDuration = node.scenarioMentors?.[0]?.animationLengthInSeconds || 1.5

    const testOptions = node.testOptions || []
    const isListenMode = node.scenarioNodeType === 16

    const multiChoiceOptions = useMemo(
        () => testOptions.filter(
            (to) => to.scenarioInteractiveElementType === InteractiveElementType.MultiChoice
                || to.interactiveElementType === InteractiveElementType.MultiChoice
        ),
        [testOptions]
    )

    const listenOption = useMemo(
        () => testOptions.find(
            (to) => (to.scenarioInteractiveElementType || to.interactiveElementType) === InteractiveElementType.ListenButtonWithSlider
                || (to.scenarioInteractiveElementType || to.interactiveElementType) === InteractiveElementType.ListenButton
        ),
        [testOptions]
    )

    const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0)
    const [disabledAlternatives, setDisabledAlternatives] = useState(new Set())
    const [correctIdx, setCorrectIdx] = useState(null)
    const [lastWrongIdx, setLastWrongIdx] = useState(null)
    const [isAdvancing, setIsAdvancing] = useState(false)
    const interactiveAnswersRef = useRef([])
    const interactiveResultsRef = useRef([])

    // Listen slider state
    const listenAudioRef = useRef(null)
    const listenIntervalRef = useRef(null)
    const [listenPlaying, setListenPlaying] = useState(false)
    const [listenProgress, setListenProgress] = useState(0)
    const [listenDuration, setListenDuration] = useState(0)
    const [listenCurrentTime, setListenCurrentTime] = useState(0)

    const currentQuestion = multiChoiceOptions[currentQuestionIndex]
    const alternatives = currentQuestion?.alternatives || []

    const shuffledOrder = useMemo(() => {
        const indices = alternatives.map((_, i) => i)
        for (let i = indices.length - 1; i > 0; i--) {
            const j = Math.floor(Math.random() * (i + 1))
            ;[indices[i], indices[j]] = [indices[j], indices[i]]
        }
        return indices
    }, [currentQuestionIndex, alternatives.length])

    // Reset per-question state when advancing to next question
    useEffect(() => {
        setDisabledAlternatives(new Set())
        setCorrectIdx(null)
        setLastWrongIdx(null)
        setIsAdvancing(false)
        interactiveAnswersRef.current = []
        questionStartTime.current = Date.now()
    }, [currentQuestionIndex])

    // Listen mode: init and auto-play audio
    const listenBlobUrl = useMemo(() => {
        if (!isListenMode || !listenOption) return null
        const url = listenOption.textAudioUrl
        return url ? (audioMap[url] || null) : null
    }, [isListenMode, listenOption, audioMap])

    useEffect(() => {
        if (!listenBlobUrl) return
        const audio = new Audio(listenBlobUrl)
        listenAudioRef.current = audio

        audio.onloadedmetadata = () => setListenDuration(audio.duration)
        audio.onended = () => {
            setListenPlaying(false)
            setListenProgress(100)
            if (listenIntervalRef.current) clearInterval(listenIntervalRef.current)
        }

        audio.play().then(() => {
            setListenPlaying(true)
            listenIntervalRef.current = setInterval(() => {
                if (listenAudioRef.current) {
                    const ct = listenAudioRef.current.currentTime
                    const dur = listenAudioRef.current.duration || 1
                    setListenCurrentTime(ct)
                    setListenProgress((ct / dur) * 100)
                }
            }, 100)
        }).catch(() => setListenPlaying(false))

        return () => {
            audio.pause()
            audio.src = ''
            if (listenIntervalRef.current) clearInterval(listenIntervalRef.current)
        }
    }, [listenBlobUrl])

    const handleListenPlayPause = useCallback(() => {
        if (!listenAudioRef.current) return
        if (listenPlaying) {
            listenAudioRef.current.pause()
            setListenPlaying(false)
            if (listenIntervalRef.current) clearInterval(listenIntervalRef.current)
        } else {
            listenAudioRef.current.play().then(() => {
                setListenPlaying(true)
                listenIntervalRef.current = setInterval(() => {
                    if (listenAudioRef.current) {
                        const ct = listenAudioRef.current.currentTime
                        const dur = listenAudioRef.current.duration || 1
                        setListenCurrentTime(ct)
                        setListenProgress((ct / dur) * 100)
                    }
                }, 100)
            }).catch(() => setListenPlaying(false))
        }
    }, [listenPlaying])

    // Advance to next question or complete the node
    const advanceToNext = useCallback(() => {
        if (currentQuestionIndex < multiChoiceOptions.length - 1) {
            setCurrentQuestionIndex((prev) => prev + 1)
        } else {
            // Stop listen audio
            if (listenAudioRef.current) {
                listenAudioRef.current.pause()
                listenAudioRef.current.src = ''
            }
            if (listenIntervalRef.current) clearInterval(listenIntervalRef.current)
            audioPlayer.stopAudio()
            playAfterAudioPlayer.stopAudio()

            onNodeComplete(
                {
                    nodeId: node.nodeId,
                    chosenAlternativeIndex: 0,
                },
                interactiveResultsRef.current
            )
        }
    }, [currentQuestionIndex, multiChoiceOptions.length, node.nodeId, onNodeComplete, audioPlayer, playAfterAudioPlayer])

    const handleSelect = useCallback(
        (origIdx) => {
            if (isAdvancing || correctIdx !== null) return
            if (disabledAlternatives.has(origIdx)) return

            const alt = alternatives[origIdx]
            const isCorrect = alt?.correct === true

            const timeSpent = (Date.now() - questionStartTime.current) / 1000
            const answer = {
                testOptionId: currentQuestion.testOptionId,
                answer: alt?.text || '',
                correct: isCorrect,
                relativeTestOptionTime: timeSpent,
            }
            interactiveAnswersRef.current.push(answer)

            if (isCorrect || isExamMode) {
                setCorrectIdx(origIdx)
                setLastWrongIdx(null)
                setIsAdvancing(true)

                const evaluation = evaluateInteractive(currentQuestion, interactiveAnswersRef.current, isExamMode)
                interactiveResultsRef.current.push({
                    ...evaluation,
                    answers: [...interactiveAnswersRef.current],
                })

                // Play "after" audio if configured on correct answers
                const playAfterUrl = currentQuestion.audioPlayType === AudioPlayType.PlayAfter
                    && currentQuestion.textAudioUrl
                    ? (audioMap[currentQuestion.textAudioUrl] || null)
                    : null

                if (playAfterUrl && isCorrect) {
                    playAfterAudioPlayer.playAudio(playAfterUrl).then(() => {
                        setTimeout(advanceToNext, 200)
                    })
                } else {
                    setTimeout(advanceToNext, 1200)
                }
            } else {
                // Wrong answer: disable this alternative, let user retry
                setLastWrongIdx(origIdx)
                setDisabledAlternatives((prev) => new Set([...prev, origIdx]))

                // Clear the wrong highlight after a short delay
                setTimeout(() => setLastWrongIdx(null), 800)
            }
        },
        [
            isAdvancing,
            correctIdx,
            disabledAlternatives,
            alternatives,
            currentQuestion,
            isExamMode,
            audioMap,
            advanceToNext,
            playAfterAudioPlayer,
        ]
    )

    const allTexts = (node.texts || []).filter(
        (t) => !isNativeSpeaker || !l2LanguageId || t.textLanguageId === l2LanguageId || !t.textLanguageId
    )
    const titleTexts = allTexts.filter(
        (t) => t.textType === 1 || t.textType === 2
    )

    const questionImageUrl = currentQuestion?.imageUrl
        ? (imageMap[currentQuestion.imageUrl] || null)
        : null

    if (!currentQuestion) {
        return (
            <div className={classes.container}>
                <Typography className={classes.titleText}>
                    No questions found
                </Typography>
                <button
                    className={classes.continueButton}
                    onClick={() =>
                        onNodeComplete({
                            nodeId: node.nodeId,
                            chosenAlternativeIndex: 0,
                        })
                    }
                >
                    Continue
                </button>
            </div>
        )
    }

    return (
        <div className={classes.container}>
            <div className={classes.sceneContainer}>
                <SceneBackground
                    node={node}
                    previousNode={previousNode}
                    imageMap={imageMap}
                    duration={animationDuration}
                />
            </div>

            {/* Header */}
            <div className={classes.headerSection}>
                {titleTexts.map((t, i) => (
                    <Typography key={i} className={classes.titleText}>
                        {renderFormattedText(t.text)}
                    </Typography>
                ))}
                {multiChoiceOptions.length > 1 && (
                    <Typography className={classes.progressText}>
                        {currentQuestionIndex + 1} / {multiChoiceOptions.length}
                    </Typography>
                )}
            </div>

            {/* Listen slider for ListenMultiChoice */}
            {isListenMode && listenBlobUrl && (
                <div className={classes.listenRow}>
                    <button className={classes.listenButton} onClick={handleListenPlayPause}>
                        {listenPlaying ? (
                            <PauseIcon className={classes.listenIcon} />
                        ) : (
                            <PlayArrowIcon className={classes.listenIcon} />
                        )}
                    </button>
                    <div className={classes.sliderContainer}>
                        <div className={classes.sliderTrack}>
                            <div
                                className={classes.sliderFill}
                                style={{ width: `${listenProgress}%` }}
                            />
                        </div>
                        <Typography className={classes.timeLabel}>
                            {formatTime(listenCurrentTime)} / {formatTime(listenDuration)}
                        </Typography>
                    </div>
                </div>
            )}

            {/* Question card */}
            <div className={classes.questionCard}>
                {questionImageUrl && (
                    <img
                        src={questionImageUrl}
                        alt=""
                        className={classes.questionImage}
                    />
                )}
                {currentQuestion.text && (
                    <Typography className={classes.questionText}>
                        {renderFormattedText(currentQuestion.text)}
                    </Typography>
                )}
            </div>

            {/* Options */}
            <div className={classes.optionsContainer}>
                {shuffledOrder.map((origIdx) => {
                    const alt = alternatives[origIdx]
                    if (!alt) return null

                    const isDisabled = disabledAlternatives.has(origIdx)
                    const isWrongJustSelected = lastWrongIdx === origIdx
                    const isCorrectSelected = correctIdx === origIdx

                    let extraClass = ''
                    if (isExamMode) {
                        if (isCorrectSelected) {
                            extraClass = classes.optionExamSelected
                        } else if (isDisabled) {
                            extraClass = classes.optionDimmed
                        }
                    } else {
                        if (isCorrectSelected) {
                            extraClass = classes.optionCorrect
                        } else if (isWrongJustSelected) {
                            extraClass = classes.optionIncorrect
                        } else if (isDisabled) {
                            extraClass = classes.optionDimmed
                        } else if (correctIdx !== null) {
                            extraClass = classes.optionDimmed
                        }
                    }

                    return (
                        <button
                            key={origIdx}
                            className={`${classes.optionButton} ${extraClass}`}
                            onClick={() => handleSelect(origIdx)}
                            disabled={isDisabled || correctIdx !== null}
                        >
                            {alt.text || `Option ${origIdx + 1}`}
                        </button>
                    )
                })}
            </div>

            {/* Feedback */}
            {correctIdx !== null && !isExamMode && (
                <Typography className={`${classes.feedbackText} ${classes.feedbackCorrect}`}>
                    Correct!
                </Typography>
            )}
            {lastWrongIdx !== null && !isExamMode && (
                <Typography className={`${classes.feedbackText} ${classes.feedbackIncorrect}`}>
                    Incorrect
                </Typography>
            )}
        </div>
    )
}

export default MultiChoiceNode
